import { AxiosError } from 'axios';

import { apiService } from '../../../shared/services/apiServices';
import {
  EQUIPMENT_ERROR_MESSAGES,
  FACILITY_CREATE_ERROR_MESSAGES,
  FACILITY_VIEW_ERROR_MESSAGES,
} from '../errors';
import { EQUIPMENT_ERROR_MESSAGES as TL } from '../errors/translations/errorMessages';
import facilityEndpoints from './facilityEndpoints';

// ── finalizeFacility ──────────────────────────────────────────────────────────

export interface FinalizeFacilityResult {
  success: boolean;
  facilityId?: string;
  error?: string;
  isNetworkError?: boolean;
  conflictDetails?: { employeeId: string; userId: string; email: string }[];
  serialNumberConflict?: string;
}

// ── Shared response types ────────────────────────────────────────────────────

export interface TempFacilityResult {
  success: boolean;
  facilityId?: string;
  error?: string;
}

export interface PatchFacilityFields {
  name: string;
  nip: string;
  phoneNumber: string;
  countryCode: string;
  addressLine1: string;
  city: string;
  state: string;
  country: string;
  addressLine2?: string | undefined;
  zipCode?: string | undefined;
}

interface PatchFacilityResponse {
  facility: { id: string; facilityId: string };
}

export interface AddFacilityResult {
  success: boolean;
  facilityId?: string;
  error?: string;
  isSubscriptionLimitReached?: boolean;
}

interface CreateFacilityResponse {
  facility: {
    id: string;
    facilityId?: string;
  };
}

const isLimitError = (msg: string | undefined): boolean => {
  if (!msg) {
    return false;
  }
  const lower = msg.toLowerCase();
  return (
    lower.includes('facility limit') ||
    lower.includes('subscription limit') ||
    lower.includes('maximum') ||
    lower.includes('limit exceeded') ||
    lower.includes('facility_limit')
  );
};

const extractErrorMessage = (errorData: unknown): string => {
  if (!errorData) {
    return EQUIPMENT_ERROR_MESSAGES.DEFAULT_ERROR;
  }
  if (typeof errorData === 'string') {
    return errorData;
  }
  if (typeof errorData === 'object') {
    const apiError = errorData as { message?: string; error?: string };
    const msg = apiError.message || apiError.error;
    if (msg === 'Internal Server Error') {
      return EQUIPMENT_ERROR_MESSAGES.UNEXPECTED_ERROR;
    }
    if (msg?.startsWith('Access denied: action=facility_create')) {
      return FACILITY_CREATE_ERROR_MESSAGES.ACCESS_DENIED;
    }
    return msg || EQUIPMENT_ERROR_MESSAGES.DEFAULT_ERROR;
  }
  return EQUIPMENT_ERROR_MESSAGES.DEFAULT_ERROR;
};

// ── createTempFacility ────────────────────────────────────────────────────────

export const createTempFacility = async (
  clientId: string
): Promise<TempFacilityResult> => {
  try {
    const result = await apiService.postAPI(
      facilityEndpoints.FACILITY_TEMP(clientId),
      {},
      { skipGlobalErrorRedirect: true }
    );

    if (result.error) {
      if (result.status === 403) {
        return {
          success: false,
          error: FACILITY_VIEW_ERROR_MESSAGES.NO_PERMISSION_TOOLTIP,
        };
      }
      return {
        success: false,
        error: result.isNetworkError
          ? EQUIPMENT_ERROR_MESSAGES.NETWORK_ERROR
          : extractErrorMessage(result.data),
      };
    }

    const data = result.data as { facility: { id: string } };
    return { success: true, facilityId: data.facility.id };
  } catch (error) {
    if (error instanceof AxiosError) {
      const isNetworkError = !error.response && !!error.request;
      return {
        success: false,
        error: isNetworkError
          ? EQUIPMENT_ERROR_MESSAGES.NETWORK_ERROR
          : extractErrorMessage(error.response?.data),
      };
    }
    return {
      success: false,
      error:
        error instanceof Error
          ? error.message
          : EQUIPMENT_ERROR_MESSAGES.UNEXPECTED_ERROR,
    };
  }
};

// ── patchFacility ─────────────────────────────────────────────────────────────

export const patchFacility = async (
  facilityId: string,
  fields: PatchFacilityFields,
  fillMandatory: boolean
): Promise<AddFacilityResult> => {
  try {
    const requestBody: Record<string, unknown> = {
      name: fields.name.trim(),
      nip: fields.nip.trim(),
      phoneNumber: fields.phoneNumber.trim(),
      countryCode: fields.countryCode.trim(),
      addressLine1: fields.addressLine1.trim(),
      city: fields.city.trim(),
      state: fields.state.trim(),
      country: fields.country.trim(),
      fillMandatory,
    };

    if (fields.addressLine2 !== undefined) {
      requestBody['addressLine2'] = fields.addressLine2.trim();
    }
    if (fields.zipCode !== undefined) {
      requestBody['zipCode'] = fields.zipCode.trim();
    }

    const result = await apiService.patchAPI(
      facilityEndpoints.FACILITY_PATCH(facilityId),
      requestBody,
      { skipGlobalErrorRedirect: true }
    );

    if (result.error) {
      if (result.status === 403) {
        return {
          success: false,
          error: FACILITY_VIEW_ERROR_MESSAGES.NO_PERMISSION_TOOLTIP,
        };
      }
      return {
        success: false,
        error: result.isNetworkError
          ? EQUIPMENT_ERROR_MESSAGES.NETWORK_ERROR
          : extractErrorMessage(result.data),
      };
    }

    const data = result.data as PatchFacilityResponse;
    return { success: true, facilityId: data.facility.id };
  } catch (error) {
    if (error instanceof AxiosError) {
      const isNetworkError = !error.response && !!error.request;
      return {
        success: false,
        error: isNetworkError
          ? EQUIPMENT_ERROR_MESSAGES.NETWORK_ERROR
          : extractErrorMessage(error.response?.data),
      };
    }
    return {
      success: false,
      error:
        error instanceof Error
          ? error.message
          : EQUIPMENT_ERROR_MESSAGES.UNEXPECTED_ERROR,
    };
  }
};

// ── addFacility ───────────────────────────────────────────────────────────────

export const addFacility = async (
  clientId: string,
  name: string,
  nip: string,
  phoneNumber: string,
  countryCode: string,
  addressLine1: string,
  city: string,
  state: string,
  country: string,
  addressLine2?: string,
  zipCode?: string
): Promise<AddFacilityResult> => {
  try {
    const requestBody: {
      name: string;
      nip: string;
      phoneNumber: string;
      countryCode: string;
      addressLine1: string;
      city: string;
      state: string;
      country: string;
      addressLine2?: string;
      zipCode?: string;
    } = {
      name: name.trim(),
      nip: nip.trim(),
      phoneNumber: phoneNumber.trim(),
      countryCode: countryCode.trim(),
      addressLine1: addressLine1.trim(),
      city: city.trim(),
      state: state.trim(),
      country: country.trim(),
    };

    if (addressLine2?.trim()) {
      requestBody.addressLine2 = addressLine2.trim();
    }
    if (zipCode?.trim()) {
      requestBody.zipCode = zipCode.trim();
    }

    const createResult = await apiService.postAPI(
      facilityEndpoints.FACILITIES(clientId),
      requestBody,
      { skipGlobalErrorRedirect: true }
    );

    if (createResult.error) {
      if (createResult.status === 403) {
        return {
          success: false,
          error: FACILITY_VIEW_ERROR_MESSAGES.NO_PERMISSION_TOOLTIP,
        };
      }
      const errorMsg = createResult.isNetworkError
        ? EQUIPMENT_ERROR_MESSAGES.NETWORK_ERROR
        : extractErrorMessage(createResult.data);
      return {
        success: false,
        error: errorMsg,
        isSubscriptionLimitReached: isLimitError(errorMsg),
      };
    }

    const data = createResult.data as CreateFacilityResponse;
    return {
      success: true,
      facilityId: data.facility.id,
    };
  } catch (error) {
    if (error instanceof AxiosError) {
      const isNetworkError = !error.response && !!error.request;
      if (isNetworkError) {
        return {
          success: false,
          error: EQUIPMENT_ERROR_MESSAGES.NETWORK_ERROR,
        };
      }
      return {
        success: false,
        error: extractErrorMessage(error.response?.data),
      };
    }
    return {
      success: false,
      error:
        error instanceof Error
          ? error.message
          : EQUIPMENT_ERROR_MESSAGES.UNEXPECTED_ERROR,
    };
  }
};

// ── getDraftFacility ──────────────────────────────────────────────────────────

export interface DraftFacilityFields {
  name: string | null;
  nip: string | null;
  phoneNumber: string | null;
  countryCode: string | null;
  addressLine1: string | null;
  addressLine2: string | null;
  city: string | null;
  state: string | null;
  zipCode: string | null;
  country: string | null;
}

export interface GetDraftFacilityResult {
  success: boolean;
  fields?: DraftFacilityFields;
  error?: string;
}

export const getDraftFacility = async (
  facilityId: string
): Promise<GetDraftFacilityResult> => {
  try {
    const result = await apiService.getAPI(
      facilityEndpoints.FACILITY_DRAFT_GET(facilityId)
    );
    if (result.error) {
      return { success: false, error: extractErrorMessage(result.data) };
    }
    const data = result.data as { facility: DraftFacilityFields };
    return { success: true, fields: data.facility };
  } catch {
    return {
      success: false,
      error: EQUIPMENT_ERROR_MESSAGES.UNEXPECTED_ERROR,
    };
  }
};

// ── finalizeFacility ──────────────────────────────────────────────────────────

interface FinalizeApiResponse {
  facility: { id: string; facilityId: string };
}

interface ConflictApiResponse {
  details?: {
    conflicts?: { employeeId: string; userId: string; email: string }[];
    serialNumber?: string | null;
  };
}

export const finalizeFacility = async (
  clientId: string,
  facilityId: string
): Promise<FinalizeFacilityResult> => {
  try {
    const result = await apiService.postAPI(
      facilityEndpoints.FACILITY_FINALIZE(clientId, facilityId),
      {},
      { skipGlobalErrorRedirect: true }
    );

    if (result.error) {
      if (result.status === 403) {
        return {
          success: false,
          error: FACILITY_VIEW_ERROR_MESSAGES.NO_PERMISSION_TOOLTIP,
        };
      }
      if (result.status === 409) {
        const conflictData = result.data as ConflictApiResponse;
        if (conflictData?.details?.conflicts) {
          return {
            success: false,
            conflictDetails: conflictData.details.conflicts,
          };
        }
        if (conflictData?.details?.serialNumber !== undefined) {
          return conflictData.details.serialNumber != null
            ? {
                success: false,
                serialNumberConflict: conflictData.details.serialNumber,
              }
            : {
                success: false,
                error: TL.SERIAL_NUMBER_DUPLICATE.polish,
              };
        }
      }
      const msg = result.isNetworkError
        ? EQUIPMENT_ERROR_MESSAGES.NETWORK_ERROR
        : extractErrorMessage(result.data);
      return {
        success: false,
        ...(result.isNetworkError && { isNetworkError: true }),
        error:
          msg === EQUIPMENT_ERROR_MESSAGES.DEFAULT_ERROR
            ? EQUIPMENT_ERROR_MESSAGES.UNEXPECTED_ERROR
            : msg,
      };
    }

    const data = result.data as FinalizeApiResponse;
    return { success: true, facilityId: data.facility.facilityId };
  } catch (error) {
    if (error instanceof AxiosError) {
      if (error.response?.status === 409) {
        const conflictData = error.response.data as ConflictApiResponse;
        if (conflictData?.details?.conflicts) {
          return {
            success: false,
            conflictDetails: conflictData.details.conflicts,
          };
        }
        if (conflictData?.details?.serialNumber !== undefined) {
          return conflictData.details.serialNumber != null
            ? {
                success: false,
                serialNumberConflict: conflictData.details.serialNumber,
              }
            : {
                success: false,
                error: TL.SERIAL_NUMBER_DUPLICATE.polish,
              };
        }
      }
      const isNetworkError = !error.response && !!error.request;
      const msg = isNetworkError
        ? EQUIPMENT_ERROR_MESSAGES.NETWORK_ERROR
        : extractErrorMessage(error.response?.data);
      return {
        success: false,
        isNetworkError,
        error:
          msg === EQUIPMENT_ERROR_MESSAGES.DEFAULT_ERROR
            ? EQUIPMENT_ERROR_MESSAGES.UNEXPECTED_ERROR
            : msg,
      };
    }
    return {
      success: false,
      error:
        error instanceof Error
          ? error.message
          : EQUIPMENT_ERROR_MESSAGES.UNEXPECTED_ERROR,
    };
  }
};
